import { IApiBaseProps, IResultResponse } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse, IListResponse } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ICreateProjectTransmittalItemRevisionRequest,
  IProjectTransmittalItemRevision,
  IUpsertProjectTransmittalItemRevisionRequest,
} from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import {
  ICheckDocNoxRequest,
  IProjectTransmittalItem,
  IUpsertProjectTransmittalItemAndRevisionRequest,
} from './transmittalItem.models';

import {
  deleteTransmittalItemAsync,
  deleteTransmittalItemRevisionsAsync,
  getTransmittalItemsAsync,
  getTransmittalItemsDrawingsAsync,
  getTransmittalItemsLatestRevisionsAsync,
  IGetTransmittalItemsDrawingsProps,
  IGetTransmittalItemsLatestRevisionProps,
  IGetTransmittalItemsProps,
  IUpdateProjectTransmittalItemProps,
  postTransmittalItemDocNosCheck,
  postTransmittalItemRemoveFromUseAsync,
  postTransmittalItemReuseAsync,
  postTransmittalItemRevisionsAsync,
  putTransmittalItemAsync,
  putTransmittalItemRevisionsAsync,
  upsertTransmittalItemsAndRevisionsAsync,
} from './transmittalItem.services';

// transmittal item revision
export const useCreateProjectTransmittalItemRevision = () => {
  return useMutation<
    IItemResponse<IProjectTransmittalItemRevision>,
    AxiosErrorResponse,
    {
      transmittalId: number;
      request: ICreateProjectTransmittalItemRevisionRequest;
    },
    unknown
  >(
    [MUTATION_KEYS.CREATE_PROJECT_TRANSMITTAL_ITEM_REVISION],
    async (props: { transmittalId: number; request: ICreateProjectTransmittalItemRevisionRequest }) => {
      const result = await postTransmittalItemRevisionsAsync(props.transmittalId, props.request);
      return result;
    },
  );
};
export const useUpdateProjectTransmittalItemRevision = () => {
  return useMutation<
    IItemResponse<IProjectTransmittalItemRevision>,
    AxiosErrorResponse,
    {
      itemRevisionId: number;
      transmittalId: number;
      request: IUpsertProjectTransmittalItemRevisionRequest;
    },
    unknown
  >(
    [MUTATION_KEYS.UPDATE_PROJECT_TRANSMITTAL_ITEM_REVISION],
    async (props: {
      itemRevisionId: number;
      transmittalId: number;
      request: IUpsertProjectTransmittalItemRevisionRequest;
    }) => {
      const result = await putTransmittalItemRevisionsAsync(props.transmittalId, props.itemRevisionId, props.request);
      return result;
    },
  );
};
export const useUpsertTransmittalItemsAndRevisions = () => {
  return useMutation<
    IListResponse<IProjectTransmittalItem>,
    AxiosErrorResponse,
    {
      transmittalId: number;
      request: IUpsertProjectTransmittalItemAndRevisionRequest[];
    },
    unknown
  >(
    [MUTATION_KEYS.UPSERT_PROJECT_TRANSMITTAL_ITEM_REVISION],
    async (props: { transmittalId: number; request: IUpsertProjectTransmittalItemAndRevisionRequest[] }) => {
      const result = await upsertTransmittalItemsAndRevisionsAsync(props.transmittalId, props.request);
      return result;
    },
  );
};

export const useDeleteProjectTransmittalItemRevision = () => {
  return useMutation<
    IItemResponse<IProjectTransmittalItemRevision>,
    AxiosErrorResponse,
    { projectId: number; itemRevisionId: number; transmittalId: number },
    unknown
  >(
    [MUTATION_KEYS.DELETE_PROJECT_TRANSMITTAL_ITEM_REVISION],
    async (props: { projectId: number; itemRevisionId: number; transmittalId: number }) => {
      const result = await deleteTransmittalItemRevisionsAsync(
        props.projectId,
        props.itemRevisionId,
        props.transmittalId,
      );
      return result;
    },
  );
};

// transmittal item
export const useGetTransmittalItemsAsync = (props: IGetTransmittalItemsProps, isDirty = false) => {
  return useQuery(
    [
      props.projectId,
      props.masterScheduleId,
      FETCH_QUERIES_NAME.TRANSMITTAL_ITEMS,
      props.transmittalId,
      props.documentTypeId,
    ],
    async () => {
      const results = await getTransmittalItemsAsync(props);
      return results.items || [];
    },
    {
      enabled:
        isPositiveNumber(props.masterScheduleId) &&
        isValidApiBaseProps(props) &&
        isPositiveNumber(props.transmittalId) &&
        !!props.documentTypeId &&
        !isDirty,
    },
  );
};

export const useUpdateTransmittalItem = () => {
  return useMutation<IItemResponse<IProjectTransmittalItem>, AxiosErrorResponse, IUpdateProjectTransmittalItemProps>(
    [MUTATION_KEYS.UPDATE_PROJECT_TRANSMITTAL_ITEM],
    async (props: IUpdateProjectTransmittalItemProps) => {
      const result = await putTransmittalItemAsync(props);
      return result;
    },
  );
};

export const useDeleteTransmittalItem = () => {
  return useMutation<boolean, AxiosErrorResponse, { transmittalId: number; transmittalItemId: number }>(
    [MUTATION_KEYS.DELETE_PROJECT_TRANSMITTAL_ITEM],
    async ({ transmittalId, transmittalItemId }: { transmittalId: number; transmittalItemId: number }) => {
      const result = await deleteTransmittalItemAsync(transmittalId, transmittalItemId);
      return result;
    },
  );
};

export const useFetchTransmittalItemsDrawings = (props: IGetTransmittalItemsDrawingsProps, isDirty = false) => {
  return useQuery(
    [props.projectId, props.masterScheduleId, FETCH_QUERIES_NAME.TRANSMITTAL_ITEMS_DRAWINGS],
    async () => {
      const results = await getTransmittalItemsDrawingsAsync(props);
      return results.items || [];
    },
    {
      enabled: isPositiveNumber(props.masterScheduleId) && isValidApiBaseProps(props) && !isDirty,
    },
  );
};

interface IProjectTransmittalItemRemoveFromUseProps {
  transmittalId: number;
  transmittalItemId: number;
  props: IApiBaseProps;
}

export const useUpdateProjectTransmittalItemRemoveFromUse = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, IProjectTransmittalItemRemoveFromUseProps>(
    [MUTATION_KEYS.UPDATE_PROJECT_TRANSMITTAL_ITEM_REMOVE_FROM_USE],
    async (props: IProjectTransmittalItemRemoveFromUseProps) => {
      const result = await postTransmittalItemRemoveFromUseAsync(
        props.transmittalId,
        props.transmittalItemId,
        props.props,
      );
      return result;
    },
  );
};

export const useUpdateProjectTransmittalItemReuse = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, IProjectTransmittalItemRemoveFromUseProps>(
    [MUTATION_KEYS.UPDATE_PROJECT_TRANSMITTAL_ITEM_REUSE],
    async (props: IProjectTransmittalItemRemoveFromUseProps) => {
      const result = await postTransmittalItemReuseAsync(props.transmittalId, props.transmittalItemId, props.props);
      return result;
    },
  );
};
export const useFetchTransmittalItemsLatestRevision = (
  props: IGetTransmittalItemsLatestRevisionProps,
  isDirty = false,
) => {
  return useQuery(
    [
      props.projectId,
      props.projectMasterScheduleId,
      props.projectMasterScheduleId,
      FETCH_QUERIES_NAME.TRANSMITTAL_ITEMS_WITH_LATEST_REVISION,
    ],
    async () => {
      const results = await getTransmittalItemsLatestRevisionsAsync(props);
      return results.items || [];
    },
    {
      enabled:
        isPositiveNumber(props.projectMasterScheduleId) &&
        isPositiveNumber(props.projectTransmittalId) &&
        isValidApiBaseProps(props) &&
        !isDirty,
    },
  );
};

export const useCheckProjectTransmittalItemDocNo = () => {
  return useMutation<boolean, AxiosErrorResponse, ICheckDocNoxRequest[]>(
    [MUTATION_KEYS.CHECK_PROJECT_TRANSMITTAL_ITEM_DOC_NO],
    async (props: ICheckDocNoxRequest[]) => {
      const result = await postTransmittalItemDocNosCheck(props);
      return result;
    },
  );
};
